import { useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Banknote, Info, Phone, Mail, Hourglass, Users } from "lucide-react";
import { CONTACT } from "../data";

const POINTS = [
  { icon: Hourglass, title: "After the first hour", text: "Beyond the first hour, time is billed in half-hour increments \u2014 every 30 minutes is £25." },
  { icon: Clock, title: "Buying time included", text: "Time spent sourcing and collecting materials is part of the rate \u2014 no hidden extras." },
  { icon: Banknote, title: "Materials charged separately", text: "Prices are for labour only. Materials are billed on top, at cost." },
  { icon: Users, title: "Extra help when needed", text: "If a job needs an assistant, I'll bring one on \u2014 added at £15 to £25 per hour, depending who's available." },
  { icon: Info, title: "Honest estimates", text: "When I give an estimate it may come out cheaper or more expensive if complications arise \u2014 but I always try to keep costs down throughout the project, with your authorisation before any extra work." },
];

export const Pricing = () => {
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <section data-testid="pricing-section" className="min-h-screen bg-brand-brown grain pt-[72px]">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-12">
          <p className="text-brand-olive font-semibold tracking-widest uppercase text-sm mb-3">
            Simple, Honest Pricing
          </p>
          <h1 className="font-display font-extrabold text-[#FCFAF6] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            What It Costs
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="pricing-panel"
          className="rounded-[2rem] bg-brand-olive p-8 sm:p-12 shadow-[0_24px_60px_rgba(0,0,0,0.35)]"
        >
          <div className="grid grid-cols-2 gap-4 pb-8 border-b border-[#FCFAF6]/20">
            <div className="text-center">
              <p className="text-[#F1EFE4] font-medium tracking-widest uppercase text-[11px] sm:text-xs mb-2">
                Call-out Charge
              </p>
              <p className="font-display font-extrabold text-[#FCFAF6] leading-none">
                <span className="text-5xl sm:text-6xl">£40</span>
              </p>
              <p className="text-[#F1EFE4]/80 text-xs sm:text-sm mt-2">even for a few-minute job</p>
            </div>
            <div className="text-center sm:border-l border-[#FCFAF6]/20">
              <p className="text-[#F1EFE4] font-medium tracking-widest uppercase text-[11px] sm:text-xs mb-2">
                Up to the First Hour
              </p>
              <p className="font-display font-extrabold text-[#FCFAF6] leading-none">
                <span className="text-5xl sm:text-6xl">£50</span>
              </p>
              <p className="text-[#F1EFE4]/80 text-xs sm:text-sm mt-2">then £25 per half hour</p>
            </div>
          </div>

          <div className="mt-8 space-y-6">
            {POINTS.map((p) => (
              <div key={p.title} className="flex items-start gap-4">
                <span className="shrink-0 w-11 h-11 rounded-xl bg-[#FCFAF6]/15 flex items-center justify-center text-[#FCFAF6]">
                  <p.icon size={22} />
                </span>
                <div>
                  <p className="font-display font-bold text-[#FCFAF6] text-lg leading-tight">{p.title}</p>
                  <p className="text-[#F1EFE4]/90 text-sm sm:text-base leading-relaxed mt-1">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={CONTACT.phoneRaw}
              data-testid="pricing-call-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-brown px-7 py-4 font-semibold text-brand-bg hover:bg-brand-brownDark hover:scale-105 transition-all shadow-lg"
            >
              <Phone size={18} /> Call {CONTACT.phoneDisplay}
            </a>
            <a
              href={CONTACT.emailLink}
              data-testid="pricing-email-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FCFAF6] px-7 py-4 font-semibold text-brand-brown hover:scale-105 transition-all shadow-lg"
            >
              <Mail size={18} /> Get a Free Estimate
            </a>
          </div>
        </motion.div>

        <p className="text-center text-[#EDE6DA]/60 text-sm mt-8">
          Free estimates &mdash; T&amp;Cs apply, job needs to be worthwhile.
        </p>
      </div>
    </section>
  );
};
